import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import { Button, IconButton, Paper, Typography } from '@mui/material';
import { Box, Stack } from '@mui/system';
import { DataGrid } from '@mui/x-data-grid';
import { post } from 'api';
import dayjs from 'dayjs';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import AddPayment from 'components/SS/addPayment';
import IconifyIcon from 'components/base/IconifyIcon';
import DataGridPagination from 'components/pagination/DataGridPagination';
import Ownership from 'components/sections/account/users-permissions/Ownership';

function CustomerDetails() {
  const { state } = useLocation();
  const [open, setOpen] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 5,
    page: 0,
  });
  const [payments, setPayments] = useState([]);
  const [payment, setPayment] = useState(null);
  const [total, setTotal] = useState(0);
  const EditButton = ({ row }) => {
    const handleView = () => {
      setOpen(true);
      setPayment(row);
    };

    return (
      <IconButton onClick={handleView} sx={{ m: 0, p: 0 }}>
        <ModeEditOutlineOutlinedIcon />
      </IconButton>
    );
  };

  const columns = useMemo(
    () => [
      {
        field: 'view',
        headerName: 'View',
        sortable: false,
        filterable: false,
        minWidth: 0,
        renderCell: (params) => <EditButton row={params.row} />,
      },
      {
        field: 'amount',
        headerName: 'Amount',
        minWidth: 100,
        sortable: false,
        filterable: false,

        renderCell: (params) => {
          const { amount } = params.row; //
          return (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                {amount}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'mode_of_payment',
        headerName: 'Mode Of Payment',
        sortable: false,
        filterable: false,
        minWidth: 150,

        renderCell: (params) => {
          const { modeOfPayment } = params.row;
          return (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                {modeOfPayment}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'bank_name',
        headerName: 'Bank Name',
        sortable: false,
        filterable: false,
        minWidth: 150,
        renderCell: (params) => {
          const { bankName } = params.row;
          return (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                {bankName}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'details',
        headerName: 'Cheque No/Transaction Id',
        sortable: false,
        filterable: false,
        minWidth: 170,

        renderCell: (params) => {
          const { chequeNumber, transactionId } = params.row;
          return (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                {transactionId ? transactionId : chequeNumber}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'date',
        headerName: 'Date',
        sortable: false,
        filterable: false,
        minWidth: 150,

        renderCell: (params) => {
          const { dateOfReceive, dateOfCheque, modeOfPayment } = params.row;
          return (
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>
                {['cheque', 'PDC'].includes(modeOfPayment)
                  ? moment(dateOfCheque).format('DD/MM/YYYY')
                  : dateOfReceive
                    ? moment(dateOfReceive).format('DD/MM/YYYY')
                    : '-'}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        sortable: false,
        filterable: false,
        minWidth: 200,

        renderCell: (params) => {
          const {
            received,
            dateOfSubmission,
            modeOfPayment,
            dateOfReceive,
            bounced = false,
          } = params.row;
          return (
            <Stack sx={{ alignItems: 'center', gap: 1 }}>
              <Typography
                variant="body2"
                color={received ? 'success' : 'error'}
                sx={{ fontWeight: 400 }}
              >
                {bounced
                  ? 'Cheque bounced'
                  : received
                    ? `Paid on ${
                        ['cheque', 'PDC'].includes(modeOfPayment)
                          ? dateOfReceive
                            ? dayjs(dateOfReceive).format('MMM DD, YYYY')
                            : dayjs(dateOfSubmission).format('MMM DD, YYYY')
                          : dateOfReceive
                            ? dayjs(dateOfReceive).format('MMM DD, YYYY')
                            : '-'
                      }`
                    : `Due on ${
                        ['cheque', 'PDC'].includes(modeOfPayment)
                          ? dateOfSubmission
                            ? dayjs(dateOfSubmission).format('MMM DD, YYYY')
                            : '-'
                          : '-'
                      }`}
              </Typography>
            </Stack>
          );
        },
      },
      {
        field: 'audit',
        headerName: 'Last Updated By',
        sortable: false,
        filterable: false,
        minWidth: 150,
        flex: 1,
        renderCell: (params) => {
          const { audit } = params.row;
          return (
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box>
                <Typography variant="subtitle2">{`${audit?.userId.firstName} ${audit?.userId.lastName} - ${audit.action}`}</Typography>
                <Typography variant="caption">
                  {moment(audit.createdAt).format('DD/MM/YYYY HH:mm A')}
                </Typography>
              </Box>
            </Stack>
          );
        },
      },
    ],
    [],
  );

  const fetchData = async () => {
    try {
      const response = await post('/users/customer_payment', {
        customerId: state?._id,
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
      });
      if (response.status == 200) {
        setPayments(response.data.data);
        setTotal(response.data.total);
      } else {
        enqueueSnackbar('failed to fetched customer payments!', { variant: 'error' });
        setPayments([]);
        setTotal(0);
      }
    } catch (error) {
      enqueueSnackbar(error.message || 'Error', { variant: 'error' });
      setPayments([]);
      setTotal(0);
    }
  };

  useEffect(() => {
    fetchData();
  }, [paginationModel.page, paginationModel.pageSize]);
  return (
    <>
      <div>
        <Paper
          background={1}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            p: { xs: 3, md: 3 },
            // background: '#EBF7F4',
          }}
        >
          <Ownership
            name={`${state.firstName} ${state.lastName}`}
            email={state.email || state.mobile || ''}
            state={state}
            lastLoginAt="2024-11-15 15:54"
            backto="/customer"
          />
        </Paper>
        <Box sx={{ p: { xs: 3, md: 5 }, pt: { xs: 3, md: 2 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button variant="soft" shape="square" color="neutral" sx={{ gap: 0.5, p: 1 }}>
              <IconifyIcon icon="material-symbols:add-2-rounded" />
              <Box
                component="span"
                sx={{ display: { xs: 'none', sm: 'block' } }}
                onClick={() => setOpen(!open)}
              >
                Add payment
              </Box>
            </Button>
          </Box>
          <Box sx={{ width: '100%' }}>
            <DataGrid
              rows={payments}
              columns={columns}
              getRowId={(row) => row._id}
              pagination
              paginationMode="server"
              rowCount={total}
              paginationModel={paginationModel} // { page: 0, pageSize: 10 }
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[5, 10, 20]}
              getRowClassName={(params) => (params.row.bounced ? 'low-stock-row' : '')}
              slots={{
                basePagination: (props) => <DataGridPagination showFullPagination {...props} />,
              }}
            />
          </Box>
        </Box>
      </div>
      <AddPayment
        customer={state || {}}
        open={open}
        setOpen={setOpen}
        fetch={fetchData}
        payment={payment}
        setPayment={setPayment}
      />
    </>
  );
}

export default CustomerDetails;
